import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import pluralize from 'pluralize';

import { CURRENCY } from '../../../../../../../../../constants/currency';
import Badge from '../../../../../../../../../components/global/Badge';
import Button from '../../../../../../../../../components/global/Button';
import PriceDisplay from '../../../../../../../../../components/global/PriceDisplay';
import { getAsset } from '../../../../../../../../../util/assets';
import AddedToCartLabel from '../../../PhotoPurchasingOptions/AddedToCartLabel';
import { REQUIRES_PRINT_PACKAGE_ALERT_MODAL } from '../RequiresPrintPackageAlert';
import useOpenModal from '../../../../../../../../../hooks/useOpenModal';
import { getIdentityName } from '../../../../../../../../../util/identity';
import PhotoSelectionOverlayModal from './components/PhotoSelectionOverlayModal';

const propTypes = {
  className: PropTypes.string,
  readOnly: PropTypes.bool,
  photo: PropTypes.shape({
    id: PropTypes.string,
  }),
  digitalBundle: PropTypes.shape({
    id: PropTypes.string.isRequired,
    photos_count: PropTypes.number.isRequired,
    price: PropTypes.shape({
      value: PropTypes.number.isRequired,
      currency: PropTypes.oneOf(Object.values(CURRENCY)),
    }).isRequired,
  }).isRequired,
  identity: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  cartItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  onDigitalBundleToggle: PropTypes.func.isRequired,
  isAllPhotos: PropTypes.bool,
  disabled: PropTypes.bool.isRequired,
  disabledBySubscription: PropTypes.bool,
  disabledByDownload: PropTypes.bool,
  photoIdsInDownloadCart: PropTypes.arrayOf(PropTypes.string),
  isPhotoEntitled: PropTypes.bool,
  entitledPurchases: PropTypes.arrayOf(PropTypes.string),
};

const DigitalBundleOption = ({
  className,
  readOnly,
  photo,
  digitalBundle,
  cartItem,
  identity,
  albumMembershipGroup,
  onDigitalBundleToggle,
  isAllPhotos,
  disabled,
  disabledBySubscription,
  disabledByDownload,
  photoIdsInDownloadCart,
  isPhotoEntitled,
  entitledPurchases,
  watermarkSettings,
}) => {
  const [photoSelectorOpen, setPhotoSelectorOpen] = useState(false);
  const isSinglePhotoBundle = digitalBundle?.photos_count === 1;
  const openModal = useOpenModal();
  const classes = classNames({
    [className]: !!className,
    'purchasing-option-item': true,
    'purchasing-option-item-digital-bundle': true,
  });
  const name = getIdentityName(identity);

  const onAddToCart = () => {
    if (disabled) {
      openModal(REQUIRES_PRINT_PACKAGE_ALERT_MODAL, {
        name: 'Digital Bundle',
      });
      return;
    }

    if (cartItem) {
      onDigitalBundleToggle();
    } else if (isSinglePhotoBundle) {
      onDigitalBundleToggle({ photos: [photo] });
    } else {
      setPhotoSelectorOpen(true);
    }
  };

  return (
    <div className={classes}>
      <div>
        <div className="purchasing-option-item-info-extra">
          <Badge
            badgeStyle="yellow"
            className="purchasing-option-item-info-badge"
          >
            <PriceDisplay
              price={digitalBundle.price.value}
              currency={digitalBundle.price.currency}
              trimDecimalZeros={false}
            />
          </Badge>
        </div>

        <div className="purchasing-option-item-thumb">
          <img width={75} src={getAsset('/photopass.svg')} alt="" />
        </div>
      </div>

      <div className="purchasing-option-item-content-wrap">
        <div className="purchasing-option-item-title">
          {isSinglePhotoBundle
            ? 'Individual Download'
            : `Digital Bundle: ${pluralize(
                'photo',
                digitalBundle.photos_count,
                true,
              )}`}
        </div>

        <div className="purchasing-option-item-content mb-2">
          <p className="text-sm lh-normal text-dark text-bold">
            Select{' '}
            {isSinglePhotoBundle
              ? 'this'
              : pluralize('photo', digitalBundle.photos_count, true)}{' '}
            of {name} to download in hi-res.
          </p>

          {disabled && !readOnly && (
            <span className="purchasing-option-item-info-requires-package">
              {(() => {
                if (disabledBySubscription) {
                  return 'A subscription is already in your cart. All photos are included with your subscription.';
                }

                if (disabledByDownload) {
                  return 'This photo is already in your cart as an individual download.';
                }

                return 'Requires the purchase of a print package first.';
              })()}
            </span>
          )}
        </div>

        {!readOnly && !disabledBySubscription && !disabledByDownload && (
          <Fragment>
            <Button
              pill={!isSinglePhotoBundle}
              btnStyle={classNames({
                primary: !cartItem,
                info: !!cartItem,
              })}
              btnOutline={!cartItem && !isSinglePhotoBundle}
              className={classNames({
                'purchasing-options-item-btn': true,
                'btn-purchase-options-in-cart': !!cartItem,
              })}
              onClick={onAddToCart}
              data-testid="btn-digital-bundle"
              disabled={isPhotoEntitled}
            >
              {cartItem ? (
                <AddedToCartLabel />
              ) : (
                <Fragment>
                  {(() => {
                    if (isPhotoEntitled) {
                      return 'You already own this photo';
                    }

                    if (isSinglePhotoBundle) {
                      return 'Add to cart';
                    }

                    return 'Select Photos';
                  })()}
                </Fragment>
              )}
            </Button>
            <PhotoSelectionOverlayModal
              forceOpen={photoSelectorOpen}
              onClose={() => setPhotoSelectorOpen(false)}
              maxPhotos={digitalBundle.photos_count}
              albumMembershipGroupId={
                isAllPhotos ? undefined : albumMembershipGroup?.id
              }
              albumId={isAllPhotos ? albumMembershipGroup?.id : undefined}
              currentSelectedPhotos={
                photo && !photoIdsInDownloadCart.includes(photo.id)
                  ? [photo]
                  : []
              }
              identityId={
                digitalBundle.is_multi_identity ? undefined : identity?.id
              }
              onComplete={({ photos }) => onDigitalBundleToggle({ photos })}
              photoIdsInDownloadCart={photoIdsInDownloadCart}
              entitledPurchases={entitledPurchases}
              watermarkSettings={watermarkSettings}
            />
          </Fragment>
        )}
      </div>
    </div>
  );
};

DigitalBundleOption.propTypes = propTypes;

export default DigitalBundleOption;
